import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatPaiseExact, formatPriceInr } from '../../utils/formatPrice';
import type { BillingPlan } from '../../services/billingApi';

interface PlanGridProps {
  plans: BillingPlan[];
  selected: BillingPlan | null;
  onSelect: (plan: BillingPlan) => void;
}

const TIER_LABEL: Record<string, string> = {
  pro: 'Pro',
  max: 'Max',
};

const TIER_ORDER = ['pro', 'max'];
const PERIOD_ORDER: BillingPlan['billing_period'][] = ['monthly', 'annual'];

/**
 * Props-only 2x2 pricing grid: tier (pro/max) x billing_period (monthly/annual).
 * Pure presentation — no data fetching, no side effects — so it can be
 * rendered directly in RTL without mocking axios/Firebase (house rule: jsdom
 * can't render axios-calling components).
 */
const PlanGrid: React.FC<PlanGridProps> = ({ plans, selected, onSelect }) => {
  const sorted = [...plans].sort((a, b) => {
    const tierDiff = TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier);
    if (tierDiff !== 0) return tierDiff;
    return PERIOD_ORDER.indexOf(a.billing_period) - PERIOD_ORDER.indexOf(b.billing_period);
  });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Choose a plan">
      {sorted.map((plan) => {
        const isSelected = selected?.plan_code === plan.plan_code;
        const tierLabel = TIER_LABEL[plan.tier] ?? plan.tier;
        return (
          <button
            key={plan.plan_code}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(plan)}
            className={cn(
              'relative text-left p-4 border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brass',
              isSelected
                ? 'border-brass bg-brass/10'
                : 'border-parchment-border-strong bg-parchment-card hover:bg-parchment-hover',
            )}
          >
            {plan.billing_period === 'annual' && (
              <span className="absolute top-2 right-2 text-[10px] font-plex uppercase tracking-wide text-brass-ink bg-brass/15 px-1.5 py-0.5">
                Best value
              </span>
            )}
            <div className="flex items-center gap-1.5 mb-1">
              {isSelected && <Check className="h-3.5 w-3.5 text-brass-ink" />}
              <span className="font-display text-base text-chamber-panel">{tierLabel}</span>
            </div>
            <p className="text-xs text-parchment-muted-warm mb-2">
              {plan.billing_period === 'annual'
                ? 'Annual billing'
                : plan.auto_renews === false
                  ? 'Monthly · pay month by month'
                  : 'Monthly billing'}
            </p>
            <p className="font-plex text-lg tabular-nums text-chamber-panel">
              {formatPriceInr(plan.price_inr, plan.billing_period)}
              <span className="ml-1 text-xs font-sans text-parchment-muted-warm">+ GST</span>
            </p>
            {plan.total_inr !== undefined && (
              <p className="text-xs tabular-nums text-parchment-muted-warm mt-0.5">
                {formatPaiseExact(plan.total_inr)} incl. 18% GST
              </p>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default PlanGrid;
